class ModelTab extends HTMLElement {
  #isBundled = false;
  #productId = null;
  #api_endpoint = 'https://iminxx-api.vercel.app/api';
  #hipAndWaistTypes = ['max sculptor', 'lounge short']; // render both hip and waist type
  #tops = ['bra', 'lounge top', 'bodysuit', 'thermal top', 'lounge dress'];
  #bottoms = [
    'cheekie',
    'shapewear',
    'short',
    'under',
    'thermal pant',
    'lounge pant',
  ];
  #hipTypes = ['thermal pant', 'cheekie', 'shapewear', 'lounge pant']; // render hip info types
  #heightTypes = ['bodysuit', 'thermal pant', 'lounge pant']; // render height info types
  #optionSeparator = ',';
  #UNAVAILABLE_TEXT = 'Unavailable';
  constructor() {
    super();
    this.#isBundled = Boolean(this.dataset.isBundled == 'true');
    this.#productId = this.dataset.productId;
    this.#api_endpoint = window.API_ENDPOINT;
  }

  connectedCallback() {
    this.renderBundleComponent().then(() => {
      // check if model contents have model item
      // if not, render the no available model element
      this.checkModelItemAvailability();
    });
  }

  async fetchGetBundleDetails() {
    if (!this.#productId) return console.warn('Product bundle ID is missing!');
    this.showLoader();
    try {
      const response = await fetch(
        `${this.#api_endpoint}/product/bundle/details/${this.#productId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': window.API_KEY,
          },
        },
      );

      const body = await response.json();

      if (body?.success) {
        return body.data;
      } else {
        return null;
      }
    } finally {
      this.hideLoader();
    }
  }

  getModelSizes(el, parentEl) {
    if (!el) return {};

    let braSizes = [];

    if (parentEl) {
      const userBraSizes = parentEl.dataset.braSize || '';

      braSizes = userBraSizes.split(',');
    }

    return {
      bodysuit: el.dataset?.bodysuitSize,
      bra: el.dataset?.braSize,
      braSizes: braSizes,
      cheekieSizes: el.dataset?.cheekieSize,
      loungeShortSizes: el.dataset?.loungeShortSize,
      loungeTop: el.dataset?.loungeTopSize,
      loungePantSizes: el.dataset?.loungePantsSize,
      maxSculptorSizes: el.dataset?.maxSculptorSize,
      shapewearSizes: el.dataset?.shapewearSize,
      thermalPants: el.dataset?.thermalPantsSize,
      thermalTop: el.dataset?.thermalTopSize,
      uk: el.dataset?.ukSize,
      height: el.dataset?.height,
      waist: el.dataset?.waist,
      hip: el.dataset?.hip,
      overbust: el.dataset?.overbust,
      underbust: el.dataset?.underbust,
      bustSize: el.dataset?.bustSize,
      loungeDressSize: el.dataset?.loungeDressSize,
    };
  }

  shouldRender(modelSizes, type) {
    const map = [
      {
        contraints: ['bra', 'lounge top', 'thermal top', 'lounge dress'],
        field: {
          size: {
            bra: 'bra',
            'lounge top': 'loungeTop',
            'thermal top': 'thermalTop',
            'lounge dress': 'loungeDressSize',
          },
          required: ['bustSize', 'underbust', 'overbust'],
        },
      },
      {
        contraints: ['cheekie', 'shapewear', 'lounge short'],
        field: {
          size: {
            cheekie: 'cheekieSizes',
            shapewear: 'shapewearSizes',
            'lounge short': 'loungeShortSizes',
          },
          required: ['uk', 'hip'],
        },
      },
      {
        contraints: ['max sculptor'],
        field: {
          size: {
            'max sculptor': 'maxSculptorSizes',
          },
          required: ['uk', 'hip', 'waist'],
        },
      },
      {
        contraints: ['bodysuit'],
        field: {
          size: {
            bodysuit: 'bodysuit',
          },
          required: ['bustSize', 'underbust', 'overbust', 'height'],
        },
      },
      {
        contraints: ['thermal pant'],
        field: {
          size: {
            'thermal pant': 'thermalPants',
          },
          required: ['height', 'uk', 'hip'],
        },
      },
    ];

    const matchedItem = map.find((item) =>
      item.contraints.some((t) => type.toLowerCase().includes(t)),
    );

    if (!matchedItem) return false;

    const matchedKey = matchedItem.contraints.find((t) =>
      type.toLowerCase().includes(t),
    );

    if (!matchedKey) return false;

    const size = modelSizes[matchedItem.field.size[matchedKey]];

    // check if theres size value
    if (!size) return false;

    // check if all required fields have values
    const fields = matchedItem.field.required.map((field) => {
      if (modelSizes[field]) return modelSizes[field];

      return null;
    });

    if (fields.includes(null)) return false;

    return true;
  }

  async renderBundleComponent() {
    try {
      if (!this.#isBundled) return;

      const data = await this.fetchGetBundleDetails();
      if (data) {
        const bundles = data.bundles;
        const items = this.querySelectorAll('.model-size-items');

        // Remove duplicate bundles with same category
        // Using Map to keep only the first occurrence of each category
        const uniqueBundlesMap = new Map();
        for (const bundle of bundles) {
          if (!uniqueBundlesMap.has(bundle.type)) {
            uniqueBundlesMap.set(bundle.type, bundle);
          } else {
            const presentCategory = uniqueBundlesMap.get(bundle.type);

            // merge the options of existing and new category instance
            presentCategory.options = [
              ...presentCategory.options,
              ...bundle.options,
            ];

            uniqueBundlesMap.set(bundle.type, presentCategory);
          }
        }

        // Convert Map back to array
        const uniqueBundles = Array.from(uniqueBundlesMap.values());

        items.forEach((item) => {
          const modelListEl = item.querySelector('.model-info-item');
          const modelSizes = this.getModelSizes(modelListEl, item);
          const root = item.querySelector('.model-info-item');

          // remove root element from the dom
          root.remove();

          // copy element root and adjust content accordingly
          uniqueBundles.forEach((bundle, index) => {
            const clone = root.cloneNode(true);

            if (this.shouldRender(modelSizes, bundle.type)) {
              // todo checks here before updating node
              this.updateCloneWithBundleData(clone, bundle, modelSizes, index);

              // Append the clone to the item container
              item.appendChild(clone);
            }
          });
        });
      }
    } catch (e) {
      console.warn('renderBundleComponent:Error' + e);
    } finally {
      // removes any empty model item
      this.removeEmptyModelItem();
      // check visibility for first instance of model item
      this.checkBundleFirstNodeMediaVisibility();
    }
  }
  /**
   *
   * @param {Node} modelNode
   */
  removeEmptyModelItem() {
    const items = this.querySelectorAll('.model-size-items');
    items.forEach((item) => {
      const modelItem = item.querySelector('.model-info-item');

      // remove item
      if (!modelItem) item.remove();
    });

    // check hr stacking and last element
    const hrs = this.querySelectorAll('hr');

    hrs.forEach((hr) => {
      const nextElement = hr.nextElementSibling;
      const isLastElement = !nextElement;
      const stacksWithAnotherHr = nextElement && nextElement.tagName === 'HR';

      if (isLastElement || stacksWithAnotherHr) {
        hr.remove();
      }
    });
  }

  checkModelItemAvailability() {
    const wrapper = this.querySelector('.model-size-contents');

    if (wrapper) {
      const emptyEl = this.querySelector('.model-tab-empty-el');

      const modelItem = wrapper.querySelector('.model-info-item');

      if (!modelItem) {
        emptyEl.classList?.remove('hidden');
      }
    }
  }
  /**
   *
   * @param {Node} parentNode
   */
  checkBundleFirstNodeMediaVisibility() {
    const modelItems = this.querySelectorAll('.model-size-items');

    modelItems.forEach((item) => {
      const modelFirstModelItemNode = item.querySelector(
        '.model-info-item:first-of-type',
      );

      if (!modelFirstModelItemNode) return;

      const mediaWrapper = modelFirstModelItemNode.querySelector(
        '.model-info-item-media-wrapper',
      );

      if (!mediaWrapper)
        return console.warn('Unable to get model item media wrapper!');

      if (mediaWrapper.classList.contains('opacity-0')) {
        mediaWrapper.classList.remove('opacity-0');
      }
    });
  }

  /**
   * Gets the matched user selected sizes from the array of product options
   * @param {string[][]} options - an array of bundle options
   * @param {string[]} userPreference - an array of user preference sizes,options etc.
   */
  getMatchedOption(options, userPreference) {
    let arr = [];
    if (!Array.isArray(userPreference)) {
      arr = userPreference?.split(',').filter(Boolean);
    } else {
      arr = userPreference;
    }

    // flatten 2d array of bundle options and remove white spaces for easier filtering
    const mergedOptions = options.flat().map((i) => i.replace(/ /g, ''));

    // apply filter for user preference values
    const filteredUserSizes = arr.filter((size) =>
      mergedOptions.includes(size.replace(/ /g, '')),
    );

    return filteredUserSizes;
  }

  renderAllModelDetails() {
    const items = this.querySelectorAll('.model-size-items');

    items.forEach((item) => {
      const root = item.querySelector('.model-info-item');

      root.querySelector('#model-size-tops')?.classList.remove('hidden');
      const bottom = root.querySelector('#model-size-bottom');
      bottom?.classList.remove('hidden');
      bottom?.classList.add('mt-10');
      bottom?.querySelector('h6')?.remove();
      bottom?.querySelector('#waist')?.classList.remove('hidden');
      bottom?.querySelector('#hip')?.classList.remove('hidden');
    });
  }

  updateCloneWithBundleData(element, bundleData, modelSizes = {}, index) {
    if (element) {
      // remove hidden class
      element.classList.remove('hidden');

      // hide image for clone element index > 0
      if (index !== 0) {
        element
          .querySelector('.model-info-item-media-wrapper')
          .classList.add('opacity-0');
      }
      const category = element.querySelector('#category');

      // do not render anything on type ''
      // if (!bundleData?.type) return element.remove();

      if (!category) return console.warn('Unable to get item category!');

      category.textContent = bundleData?.type || 'Uncategorized';

      const isTop = this.isTops(bundleData);
      const isBottom = this.isBottom(bundleData);

      this.updateIminSize(element, bundleData, modelSizes);

      if (isTop) {
        const topEl = element.querySelector('#model-size-tops');

        if (!topEl) return;

        topEl.classList.remove('hidden');
        // remove bottom div element
        element.querySelector('#model-size-bottom')?.remove();
        // check if height info should be displayed
        this.checkHeightDisplay(topEl, bundleData);
      } else if (isBottom) {
        const bottomEl = element.querySelector('#model-size-bottom');
        if (!bottomEl) return;
        bottomEl.classList.remove('hidden');
        element.querySelector('#model-size-tops')?.remove();

        const hip = bottomEl.querySelector('#hip');
        const waist = bottomEl.querySelector('#waist');

        if (this.shouldShowHipAndWaist(bundleData)) {
          if (hip) {
            hip.classList.remove('hidden');
          }
          if (waist) {
            waist.classList.remove('hidden');
          }
        } else if (this.shouldShowHip(bundleData)) {
          if (hip) {
            hip.classList.remove('hidden');
          }
          if (waist) {
            waist.style = 'display: none;';
          }
        } else {
          if (waist) {
            waist.classList.remove('hidden');
          }
          if (hip) {
            hip.style = 'display: none';
          }
        }
        // check if height info should be displayed
        this.checkHeightDisplay(bottomEl, bundleData);
      } else {
        // display all  model details
        element.querySelector('#model-size-tops').classList.remove('hidden');
        element.querySelector('#model-size-bottom').classList.remove('hidden');
        // remove the first h6 avoiding duplicate size heading
        element.querySelector('#imin-size-bottom')?.remove();
      }
    }
  }

  checkHeightDisplay(element, bundleData) {
    const heightEl = element.querySelector('#height');

    if (!heightEl) return;
    // check if should show height details
    if (!this.shouldShowHeight(bundleData)) heightEl.remove();
  }

  updateIminSize(element, bundleData, modelSizes) {
    const productTypeLower = bundleData?.type?.toLowerCase();

    let size = '';

    if (productTypeLower.includes('bra')) {
      const braSizes = modelSizes.bra?.split(',').filter(Boolean);

      const braSizesMatchedFromOptions = this.getMatchedOption(
        bundleData.options,
        braSizes,
      );
      if (braSizesMatchedFromOptions.length <= 0) {
        if (braSizes.length > 0) {
          size = braSizes[0];
        } else {
          size = this.#UNAVAILABLE_TEXT;
        }
      } else {
        size = braSizesMatchedFromOptions.join(`${this.#optionSeparator} `);
      }
    } else if (productTypeLower.includes('cheekie')) {
      const cheekieSizes = modelSizes.cheekieSizes?.split(',').filter(Boolean);
      const cheekieSizesMatchedFromOptions = this.getMatchedOption(
        bundleData.options,
        cheekieSizes,
      );
      if (cheekieSizesMatchedFromOptions.length <= 0) {
        if (cheekieSizes.length > 0) {
          size = cheekieSizes[0];
        } else {
          size = this.#UNAVAILABLE_TEXT;
        }
      } else {
        size = cheekieSizesMatchedFromOptions.join(`${this.#optionSeparator} `);
      }
      // size = modelSizes.cheekie;
    } else if (productTypeLower.includes('bodysuit')) {
      size = modelSizes.bodysuit;
    } else if (productTypeLower.includes('lounge short')) {
      const sizes = modelSizes.loungeShortSizes?.split(',').filter(Boolean);
      const loungeShortSizesMatchedFromOptions = this.getMatchedOption(
        bundleData.options,
        sizes,
      );

      if (loungeShortSizesMatchedFromOptions.length <= 0) {
        if (sizes.length > 0) {
          size = sizes[0];
        } else {
          size = this.#UNAVAILABLE_TEXT;
        }
      } else {
        size = loungeShortSizesMatchedFromOptions.join(
          `${this.#optionSeparator} `,
        );
      }
      // size = modelSizes.loungeShort;
    } else if (productTypeLower.includes('lounge top')) {
      size = modelSizes.loungeTop;
    } else if (productTypeLower.includes('max sculptor')) {
      const sizes = modelSizes.maxSculptorSizes?.split(',').filter(Boolean);
      const maxSculptorSizesMatchedFromOptions = this.getMatchedOption(
        bundleData.options,
        sizes,
      );

      if (maxSculptorSizesMatchedFromOptions.length <= 0) {
        if (sizes.length > 0) {
          size = sizes[0];
        } else {
          size = this.#UNAVAILABLE_TEXT;
        }
      } else {
        size = maxSculptorSizesMatchedFromOptions.join(
          `${this.#optionSeparator} `,
        );
      }
      // size = modelSizes.maxSculptor;
    } else if (productTypeLower.includes('shapewear')) {
      const sizes = modelSizes.shapewearSizes?.split(',').filter(Boolean);

      const shapewearSizesMatchedFromOptions = this.getMatchedOption(
        bundleData.options,
        sizes,
      );

      if (shapewearSizesMatchedFromOptions.length <= 0) {
        if (sizes.length > 0) {
          size = sizes[0];
        } else {
          size = this.#UNAVAILABLE_TEXT;
        }
      } else {
        size = shapewearSizesMatchedFromOptions.join(
          `${this.#optionSeparator} `,
        );
      }
      // size = modelSizes.shapewear;
    } else if (productTypeLower.includes('thermal pant')) {
      size = modelSizes.thermalPants;
    } else if (productTypeLower.includes('thermal top')) {
      size = modelSizes.thermalTop;
    } else if (productTypeLower.includes('lounge pant')) {
      const sizes = modelSizes.loungePantSizes?.split(',').filter(Boolean);
      const loungePantSizesMatchedFromOptions = this.getMatchedOption(
        bundleData.options,
        sizes,
      );
      if (loungePantSizesMatchedFromOptions.length <= 0) {
        if (sizes.length > 0) {
          size = sizes[0];
        } else {
          size = this.#UNAVAILABLE_TEXT;
        }
      } else {
        size = loungePantSizesMatchedFromOptions.join(
          `${this.#optionSeparator} `,
        );
      }
    } else if (productTypeLower.includes('lounge dress')) {
      size = modelSizes.loungeDressSize;
    }

    const slot = element.querySelector('#size-slot');

    if (slot && size) {
      slot.textContent = `: ${size}`;
    } else if (slot) {
      slot.textContent = this.#UNAVAILABLE_TEXT;
    }
  }

  isTops(bundleData) {
    const type = bundleData?.type || '';
    return this.#tops.some((item) => type.toLowerCase().includes(item));
  }
  isBottom(bundleData) {
    const type = bundleData?.type || '';

    return this.#bottoms.some((item) => type.toLowerCase().includes(item));
  }
  shouldShowHipAndWaist(bundleData) {
    const type = bundleData?.type || '';

    return this.#hipAndWaistTypes.some((item) =>
      type.toLowerCase().includes(item),
    );
  }
  shouldShowHip(bundleData) {
    const type = bundleData?.type || '';

    return this.#hipTypes.some((item) => type.toLowerCase().includes(item));
  }
  shouldShowHeight(bundleData) {
    const type = bundleData?.type || '';

    return this.#heightTypes.some((item) => type.toLowerCase().includes(item));
  }
  showLoader() {
    const loader = this.querySelectorAll('#skeleton-wrapper');
    if (loader.length > 0) {
      loader.forEach((skel) => {
        skel.classList.remove('hidden');
      });
    }
    const heading = this.querySelector('#category');
    if (heading) {
      heading.classList.add('hidden');
    }
  }
  hideLoader() {
    const loader = this.querySelectorAll('#skeleton-wrapper');
    if (loader.length > 0) {
      loader.forEach((skel) => {
        skel.classList.add('hidden');
      });
    }
    const heading = this.querySelector('#category');
    if (heading) {
      heading.classList.remove('hidden');
    }
  }
}

customElements.define('model-tab', ModelTab);
